import * as React from 'react';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

function padTwo(n: number): string {
  return String(n).padStart(2, '0');
}

function to12h(h24: number): { hour: number; period: 'AM' | 'PM' } {
  if (h24 === 0) return { hour: 12, period: 'AM' };
  if (h24 < 12) return { hour: h24, period: 'AM' };
  if (h24 === 12) return { hour: 12, period: 'PM' };
  return { hour: h24 - 12, period: 'PM' };
}

function to24h(h12: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

function formatDateTime(date: Date, is12h: boolean): string {
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  if (is12h) {
    const { hour, period } = to12h(date.getHours());
    return `${dateStr} ${padTwo(hour)}:${padTwo(date.getMinutes())} ${period}`;
  }
  return `${dateStr} ${padTwo(date.getHours())}:${padTwo(date.getMinutes())}`;
}

export interface DateTimePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  onOpen?: () => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

export function DateTimePicker({
  value,
  onChange,
  onOpen,
  placeholder = 'Pick date & time',
  disabled,
  minDate,
  maxDate,
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [is12h, setIs12h] = React.useState(false);
  const [period, setPeriod] = React.useState<'AM' | 'PM'>('AM');
  const [hourInput, setHourInput] = React.useState(
    value ? padTwo(value.getHours()) : '00',
  );
  const [minuteInput, setMinuteInput] = React.useState(
    value ? padTwo(value.getMinutes()) : '00',
  );

  // Sync display inputs when value or format changes externally
  React.useEffect(() => {
    if (value) {
      if (is12h) {
        const { hour, period: p } = to12h(value.getHours());
        setHourInput(padTwo(hour));
        setPeriod(p);
      } else {
        setHourInput(padTwo(value.getHours()));
      }
      setMinuteInput(padTwo(value.getMinutes()));
    }
  }, [value, is12h]);

  const applyTime = React.useCallback(
    (h: string, m: string, p: 'AM' | 'PM' = period) => {
      let hNum = parseInt(h) || 0;
      if (is12h) {
        hNum = Math.min(12, Math.max(1, hNum));
        const display = padTwo(hNum);
        hNum = to24h(hNum, p);
        setHourInput(display);
      } else {
        hNum = Math.min(23, Math.max(0, hNum));
        setHourInput(padTwo(hNum));
      }
      const mNum = Math.min(59, Math.max(0, parseInt(m) || 0));
      setMinuteInput(padTwo(mNum));
      if (value) {
        let newDate = new Date(value);
        newDate.setHours(hNum, mNum, 0, 0);
        if (minDate && newDate < minDate) newDate = new Date(minDate);
        if (maxDate && newDate > maxDate) newDate = new Date(maxDate);
        // Sync inputs back if clamped
        if (is12h) {
          const { hour, period: clampedPeriod } = to12h(newDate.getHours());
          setHourInput(padTwo(hour));
          setPeriod(clampedPeriod);
        } else {
          setHourInput(padTwo(newDate.getHours()));
        }
        setMinuteInput(padTwo(newDate.getMinutes()));
        onChange?.(newDate);
      }
    },
    [is12h, period, value, onChange, minDate, maxDate],
  );

  // Flush any pending typed input before the popover closes
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      applyTime(hourInput, minuteInput);
    } else {
      onOpen?.();
    }
    setOpen(nextOpen);
  };

  const handleDaySelect = (date: Date) => {
    const newDate = new Date(date);
    let hNum = parseInt(hourInput) || 0;
    if (is12h) {
      hNum = to24h(Math.min(12, Math.max(1, hNum)), period);
    } else {
      hNum = Math.min(23, Math.max(0, hNum));
    }
    newDate.setHours(hNum, parseInt(minuteInput) || 0, 0, 0);
    onChange?.(newDate);
  };

  const togglePeriod = () => {
    const newPeriod: 'AM' | 'PM' = period === 'AM' ? 'PM' : 'AM';
    setPeriod(newPeriod);
    applyTime(hourInput, minuteInput, newPeriod);
  };

  const handleHourKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const max = is12h ? 12 : 23;
    const min = is12h ? 1 : 0;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      applyTime(
        String(Math.min(max, (parseInt(hourInput) || min) + 1)),
        minuteInput,
      );
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      applyTime(
        String(Math.max(min, (parseInt(hourInput) || min) - 1)),
        minuteInput,
      );
    }
  };

  const handleMinuteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      applyTime(
        hourInput,
        String(Math.min(59, (parseInt(minuteInput) || 0) + 1)),
      );
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      applyTime(
        hourInput,
        String(Math.max(0, (parseInt(minuteInput) || 0) - 1)),
      );
    }
  };

  const inputClass =
    'w-10 h-8 text-center text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">
            {value ? formatDateTime(value, is12h) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          selected={value}
          onSelect={handleDaySelect}
          minDate={minDate}
          maxDate={maxDate}
        />
        <div className="border-t border-border px-3 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Time</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={hourInput}
                  onChange={(e) =>
                    setHourInput(e.target.value.replace(/\D/g, '').slice(0, 2))
                  }
                  onBlur={() => applyTime(hourInput, minuteInput)}
                  onKeyDown={handleHourKeyDown}
                  disabled={!value}
                  className={inputClass}
                />
                <span className="text-sm font-medium select-none">:</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={minuteInput}
                  onChange={(e) =>
                    setMinuteInput(
                      e.target.value.replace(/\D/g, '').slice(0, 2),
                    )
                  }
                  onBlur={() => applyTime(hourInput, minuteInput)}
                  onKeyDown={handleMinuteKeyDown}
                  disabled={!value}
                  className={inputClass}
                />
                {is12h && (
                  <button
                    type="button"
                    onClick={togglePeriod}
                    disabled={!value}
                    className="w-10 h-8 text-xs font-semibold rounded-md border border-input bg-background hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {period}
                  </button>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIs12h((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-accent"
            >
              {is12h ? '24h' : '12h'}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
